import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import unionBy from 'lodash/unionBy';
import uniqBy from 'lodash/uniqBy';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';

import type { HttpRequestConfig } from '@/apis/dash.http';
import type {
  Conversation,
  MessageInteraction,
  TriggeredMessageRule,
} from '@/app/community/types/interaction';

import {
  createConversationMessage,
  getConversation,
  http as communityHttp,
  updateConversation as updateConversationApi,
  updateConversationMessages,
  updateMessage,
} from '@/apis/community-typed';
import { COMMUNITY_INTERACTION_PLATFORM_BY_TYPE } from '@/app/community/constants';
import { RULE_ACTION_KEYS } from '@/app/community/constants/inboxAutomation';
import {
  ruleExecutedMessageFormatter,
  ruleFailedMessageFormatter,
} from '@/app/community/utils/interaction-rules';
import {
  getMessageChronologicalSortValue,
  setMessageReaction,
} from '@/app/community/utils/messages';
import { communityMessageInteractionEvent } from '@/app/community/utils/mixpanel-events';
import { parseTimestamps } from '@/app/community/utils/time';
import { useCommunityInteractionRulesStore } from '@/stores/community-interaction-rules';

function isFailedDestructiveRecord(record: TriggeredMessageRule): boolean {
  if (!record.errorStatus) return false;
  return !!(
    record.action?.[RULE_ACTION_KEYS.DELETE] ||
    record.action?.[RULE_ACTION_KEYS.HIDE] ||
    record.action?.[RULE_ACTION_KEYS.PRIVATE_REPLY_PRESET_REPLY_ID]
  );
}

export const useCommunityMessagesStore = defineStore('communityMessages', () => {
  const messagesByConversationId = reactive<Record<number, MessageInteraction[]>>({});
  // Singleton refs — ConversationDetail displays one conversation at a time.
  // Not keyed by conversationId because different interactions in the same
  // conversation require different `around` windows for scroll-to-message.
  const conversation = ref<Conversation | null>(null);
  const currentPaging = ref<{ next?: string | null; previous?: string | null }>({});
  const pending = ref(false);

  const brandParticipantId = computed(() => {
    const sourceUserId = conversation.value?.brandParticipant?.sourceUserId;
    return sourceUserId ? parseInt(sourceUserId) : null;
  });

  const currentMessages = computed(() =>
    conversation.value?.id ? messagesByConversationId[conversation.value.id] : [],
  );
  const ruleExecutionRecords = computed(() => {
    const allRecords =
      currentMessages.value?.flatMap((m) =>
        (m.triggeredMessageRules ?? []).map((r) => ({ ...r, messageId: m.id })),
      ) ?? [];
    if (!allRecords.length) return [];

    const interactionRulesStore = useCommunityInteractionRulesStore();
    const failedRecords = allRecords
      .filter(isFailedDestructiveRecord)
      .map((r) => ({ ...r, isError: true }));
    const successfulRecords = allRecords.filter((r) => !isFailedDestructiveRecord(r));

    return [
      ...ruleFailedMessageFormatter(failedRecords),
      ...ruleExecutedMessageFormatter(
        successfulRecords,
        interactionRulesStore.checkRuleDeleted,
        undoRuleAction,
        removeRuleExecutionRecord,
        interactionRulesStore.openRuleBuilderToEdit,
      ),
    ].sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf());
  });

  function removeRuleExecutionRecord(record: { id: number; messageId: number }) {
    const message = currentMessages.value?.find((m) => m.id === record.messageId);
    if (message?.triggeredMessageRules) {
      message.triggeredMessageRules = message.triggeredMessageRules.filter(
        (r) => r.id !== record.id,
      );
    }
  }

  async function undoRuleAction(
    rule: TriggeredMessageRule,
    deleteRuleExecutedMessage: (record: { id: number; messageId: number }) => void,
  ) {
    const interactionRulesStore = useCommunityInteractionRulesStore();

    const wrappedDelete = (msg: { id: number; messageId: number }) => {
      deleteRuleExecutedMessage(msg);

      if (!conversation.value?.id || !rule.messageId) return;
      const patch: Partial<MessageInteraction> = {};

      if (rule.action[RULE_ACTION_KEYS.TAGS]) {
        const removeTagIds = Object.keys(
          (rule.action[RULE_ACTION_KEYS.TAGS] as Record<string, unknown>[])[0],
        ).map(Number);
        const currentMsg = currentMessages.value?.find((m) => m.id === rule.messageId);
        if (currentMsg?.tagIds) {
          patch.tagIds = currentMsg.tagIds.filter((id) => !removeTagIds.includes(id));
        }
      }

      if (Object.keys(patch).length) {
        patchMessageField(conversation.value.id, rule.messageId, patch);
      }
    };

    return interactionRulesStore.undoAction(rule, wrappedDelete);
  }

  function addRuleExecutionRecords(messageId: number, records: TriggeredMessageRule[]) {
    const message = currentMessages.value?.find((m) => m.id === messageId);
    if (message) {
      message.triggeredMessageRules = unionBy(message.triggeredMessageRules ?? [], records, 'id');
    }
  }

  function sortMessageInteractions(messages: MessageInteraction[]): MessageInteraction[] {
    return sortBy(messages, [(m) => getMessageChronologicalSortValue(m), (m) => m.id]);
  }

  async function getConversationMessages(
    {
      conversationId,
      limit,
      after,
      before,
      around,
    }: {
      conversationId: number;
      limit?: number;
      after?: string;
      before?: string;
      around?: string;
    },
    config: HttpRequestConfig = {},
  ) {
    const isPageLoad = !!(after || before);
    pending.value = true;
    if (!isPageLoad) {
      messagesByConversationId[conversationId] = [];
      currentPaging.value = {};
    }
    try {
      const res = await getConversation({ conversationId, limit, after, before, around }, config);
      const { data, paging } = res.data;
      const { messages: rawMessages = [], ...conversationData } = data;
      const newMessages = rawMessages.map(parseTimestamps);
      if (!isPageLoad) {
        conversation.value = conversationData;
        currentPaging.value = paging;
      } else {
        const direction = after ? 'previous' : 'next';
        currentPaging.value = { ...currentPaging.value, [direction]: paging[direction] };
      }
      if (newMessages.length > 0) {
        const oldMessages = messagesByConversationId[conversationId] ?? [];
        messagesByConversationId[conversationId] = sortMessageInteractions(
          uniqBy([...oldMessages, ...newMessages], 'id'),
        );
      }
    } finally {
      pending.value = false;
    }
  }

  async function loadConversationPage(
    conversationId: number,
    pageUrl: string | null | undefined,
    direction: 'next' | 'previous',
  ) {
    if (!pageUrl || pending.value) return;
    pending.value = true;
    try {
      const res = await communityHttp.get(pageUrl);
      const { data, paging } = res.data;
      const { messages: rawMessages = [] } = data;
      const newMessages = rawMessages.map(parseTimestamps);
      currentPaging.value = { ...currentPaging.value, [direction]: paging[direction] };
      if (newMessages.length > 0) {
        const oldMessages = messagesByConversationId[conversationId] ?? [];
        messagesByConversationId[conversationId] = sortMessageInteractions(
          uniqBy([...oldMessages, ...newMessages], 'id'),
        );
      }
    } finally {
      pending.value = false;
    }
  }

  function clearMessages(conversationId: number) {
    delete messagesByConversationId[conversationId];
    conversation.value = null;
    currentPaging.value = {};
  }

  function updateMessageLocally(conversationId: number, updatedMessage: MessageInteraction) {
    const messages = messagesByConversationId[conversationId];
    if (messages?.length && updatedMessage) {
      const parsed = parseTimestamps(updatedMessage) as MessageInteraction;
      messagesByConversationId[conversationId] = messages.map((m) =>
        m.id === parsed.id ? parsed : m,
      );
    }
  }

  function patchMessageField(
    conversationId: number,
    messageId: number,
    patch: Partial<MessageInteraction>,
  ) {
    if (!conversationId || !messageId) return;
    const messages = messagesByConversationId[conversationId];
    if (messages?.length && patch) {
      messagesByConversationId[conversationId] = messages.map((m) =>
        m.id === messageId ? { ...m, ...patch } : m,
      );
    }
  }

  function applyBulkUpdateResponse(updatedMessages: MessageInteraction[]) {
    updatedMessages.forEach((updated) => {
      if (updated.conversationId && updated.id) {
        const parsed = parseTimestamps(updated) as Partial<MessageInteraction>;
        patchMessageField(updated.conversationId, updated.id, parsed);
      }
    });
  }

  function isLikedBy(message: MessageInteraction) {
    const loveReaction = message.reactions?.love;
    return brandParticipantId.value
      ? (loveReaction?.participantIds?.includes(brandParticipantId.value) ?? false)
      : false;
  }

  async function likeMessage(message: MessageInteraction) {
    if (!brandParticipantId.value) return;

    const liked = isLikedBy(message);

    // Optimistic update
    updateMessageLocally(
      message.conversationId,
      setMessageReaction(message, 'love', brandParticipantId.value, !liked) as MessageInteraction,
    );

    try {
      await updateMessage(message.id, { reaction: !liked ? 'love' : null });
    } catch {
      // Revert on failure
      updateMessageLocally(message.conversationId, message);
      throw new Error('Failed to update like status');
    }

    const { platform = 'UNKNOWN', messageType = 'UNKNOWN' } =
      COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[message.type] ?? {};
    communityMessageInteractionEvent({
      platform,
      messageType,
      text: null,
      actionPerformed: !liked ? 'Reaction Added' : 'Reaction Removed',
      // @ts-expect-error -- communityMessageInteractionEvent is untyped JS
      reaction: !liked ? 'love' : null,
      communityInteraction: message,
    });
  }

  async function updateConversation(
    conversationId: number,
    payload: { assigneeId?: number | null },
  ) {
    const res = await updateConversationApi(conversationId, payload);
    if (conversation.value) {
      conversation.value = { ...conversation.value, ...payload };
    }
    return res;
  }

  async function sendMessage(conversationId: number, payload: { text: string }) {
    const res = await createConversationMessage(conversationId, payload);
    const newMessage = parseTimestamps(res.data.data) as MessageInteraction;
    appendMessages(conversationId, [newMessage]);
    return newMessage;
  }

  function appendMessages(conversationId: number, incoming: MessageInteraction[]) {
    if (!incoming?.length) return;
    const existing = messagesByConversationId[conversationId];
    if (!existing) return; // Don't seed a thread we haven't loaded
    const parsed = incoming.map((m) => parseTimestamps(m)) as MessageInteraction[];
    messagesByConversationId[conversationId] = sortMessageInteractions(
      uniqBy([...parsed, ...existing], 'id'),
    );
  }

  async function closeConversationMessages(conversationId: number) {
    const res = await updateConversationMessages(conversationId, {
      closeConversationMessages: true,
    });
    const updated = res.data.data as MessageInteraction[];
    applyBulkUpdateResponse(updated);
    return updated;
  }

  return {
    messagesByConversationId,
    conversation,
    currentPaging,
    pending,
    currentMessages,
    ruleExecutionRecords,
    removeRuleExecutionRecord,
    undoRuleAction,
    addRuleExecutionRecords,
    getConversationMessages,
    loadConversationPage,
    clearMessages,
    updateMessageLocally,
    patchMessageField,
    applyBulkUpdateResponse,
    isLikedBy,
    likeMessage,
    updateConversation,
    sendMessage,
    appendMessages,
    closeConversationMessages,
  };
});
